import { useNode } from "@craftjs/core";
import React from "react";
import { Box } from "@mui/material";
import { selectedOutlineSx } from "../../config/settingsStyles";
import { SliderField, CheckboxField, ColorField, SelectField, SettingsSection } from "../ui/form/SettingsField";

export interface DividerProps {
  height?: number;
  showLine?: boolean;
  lineColor?: string;
  lineWidth?: "full" | "half" | "third";
  lineStyle?: "solid" | "dashed" | "dotted";
  lineThickness?: "regular" | "medium" | "semibold" | "bold";
  backgroundColor?: string;
}

const lineWidthMap = {
  full: "100%",
  half: "50%",
  third: "33.333%",
};

const lineThicknessMap = {
  regular: 1,
  medium: 2,
  semibold: 3,
  bold: 4,
};

export const Divider = ({
  height = 40,
  showLine = true,
  lineColor = "#e5e7eb",
  lineWidth = "full",
  lineStyle = "solid",
  lineThickness = "regular",
  backgroundColor = "transparent",
}: DividerProps) => {
  const {
    connectors: { connect, drag },
    selected,
  } = useNode((state) => ({
    selected: state.events.selected,
  }));

  // Ensure height is a valid number
  const safeHeight =
    typeof height === "number" && height > 0
      ? height
      : typeof height === "string" && parseInt(height) > 0
        ? parseInt(height)
        : 40;

  return (
    <Box
      ref={(ref: HTMLDivElement | null) => {
        if (ref) connect(drag(ref));
      }}
      sx={{
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        height: `${safeHeight}px`,
        minHeight: `${safeHeight}px`,
        flexShrink: 0,
        bgcolor: backgroundColor,
        ...(selected && selectedOutlineSx),
      }}
    >
      {showLine && (
        <Box
          sx={{
            width: lineWidthMap[lineWidth],
            height: `${lineThicknessMap[lineThickness]}px`,
            bgcolor: lineStyle === "solid" ? lineColor : "transparent",
            borderTop:
              lineStyle !== "solid" ? `${lineThicknessMap[lineThickness]}px ${lineStyle} ${lineColor}` : "none",
          }}
        />
      )}
    </Box>
  );
};

export const DividerSettings = () => {
  const {
    actions: { setProp },
    height,
    showLine,
    lineColor,
    lineWidth,
    lineStyle,
    lineThickness,
    backgroundColor,
  } = useNode((node) => ({
    height: node.data.props.height,
    showLine: node.data.props.showLine,
    lineColor: node.data.props.lineColor,
    lineWidth: node.data.props.lineWidth,
    lineStyle: node.data.props.lineStyle,
    lineThickness: node.data.props.lineThickness,
    backgroundColor: node.data.props.backgroundColor,
  }));

  return (
    <SettingsSection>
      <SliderField
        label="Height"
        value={height}
        onChange={(v) => setProp((props: DividerProps) => (props.height = v))}
        min={10}
        max={200}
        unit="px"
      />

      <CheckboxField
        label="Show Line"
        checked={showLine}
        onChange={(v) => setProp((props: DividerProps) => (props.showLine = v))}
      />

      {showLine && (
        <>
          <ColorField
            label="Line Color"
            value={lineColor}
            onChange={(v) => setProp((props: DividerProps) => (props.lineColor = v))}
          />

          <SelectField
            label="Line Width"
            value={lineWidth}
            onChange={(v) => setProp((props: DividerProps) => (props.lineWidth = v as DividerProps["lineWidth"]))}
            options={[
              { value: "full", label: "Full Width" },
              { value: "half", label: "Half Width" },
              { value: "third", label: "Third Width" },
            ]}
          />

          <SelectField
            label="Line Style"
            value={lineStyle}
            onChange={(v) => setProp((props: DividerProps) => (props.lineStyle = v as DividerProps["lineStyle"]))}
            options={[
              { value: "solid", label: "Solid" },
              { value: "dashed", label: "Dashed" },
              { value: "dotted", label: "Dotted" },
            ]}
          />

          <SelectField
            label="Line Thickness"
            value={lineThickness}
            onChange={(v) =>
              setProp((props: DividerProps) => (props.lineThickness = v as DividerProps["lineThickness"]))
            }
            options={[
              { value: "regular", label: "Regular (1px)" },
              { value: "medium", label: "Medium (2px)" },
              { value: "semibold", label: "SemiBold (3px)" },
              { value: "bold", label: "Bold (4px)" },
            ]}
          />
        </>
      )}

      <ColorField
        label="Background Color"
        value={backgroundColor === "transparent" ? "#ffffff" : backgroundColor}
        onChange={(v) => setProp((props: DividerProps) => (props.backgroundColor = v))}
        showReset
        defaultValue="transparent"
      />
    </SettingsSection>
  );
};

Divider.craft = {
  displayName: "Divider",
  props: {
    height: 40,
    showLine: true,
    lineColor: "#e5e7eb",
    lineWidth: "full",
    lineStyle: "solid",
    lineThickness: "regular",
    backgroundColor: "transparent",
  },
  related: {
    settings: DividerSettings,
  },
};
